import { S } from '../core/game';
import { toast, celebrate } from '../core/effects';
import { bus } from '../core/bus';
import { m } from '../i18n';
import { WORDS } from './vocab';

/**
 * The English passport: everything the player practises leaves a trace here,
 * adding up to experience points, a level, and stamps for role-plays and trips.
 */
export function dayNumber(): number {
  const s = S();
  return s.week * 3 + s.dayIndex;
}

function today() {
  const e = S().english;
  const d = dayNumber();
  return (e.days[d] ??= { words: 0, talks: 0, spells: 0, mins: 0 });
}

/** XP needed for each level (index = level). */
export const LEVELS = [0, 40, 120, 260, 450, 700] as const;

export interface Progress {
  xp: number;
  level: number;
  /** 0..1 towards the next level (1 at the top level). */
  toNext: number;
  known: number;
  mastered: number;
  spelled: number;
  shadowed: number;
  talks: number;
  perfectTalks: number;
  stories: number;
  stamps: number;
}

export function progress(): Progress {
  const s = S();
  const e = s.english;
  const cards = Object.values(s.vocab);
  const known = cards.length;
  const mastered = cards.filter((c) => c.box >= 3).length;
  const spelled = Object.keys(e.spelled).length;
  const shadowed = Object.keys(e.shadowed).length;
  const talkStars = Object.values(e.talks);
  const stories = Object.keys(e.stories).length;
  const stamps = Object.keys(e.stamps).length;
  const xp =
    known * 2 + mastered * 4 + spelled * 3 + shadowed * 3 +
    talkStars.reduce((a, b) => a + b * 4, 0) +
    Object.values(e.stories).reduce((a, b) => a + b * 5, 0) +
    stamps * 6;
  let level = 0;
  while (level < LEVELS.length - 1 && xp >= LEVELS[level + 1]) level++;
  const lo = LEVELS[level], hi = LEVELS[level + 1];
  return {
    xp, level, toNext: hi === undefined ? 1 : (xp - lo) / (hi - lo),
    known, mastered, spelled, shadowed,
    talks: talkStars.length, perfectTalks: talkStars.filter((v) => v >= 3).length,
    stories, stamps,
  };
}

/** Announce a level-up after any change that adds XP. */
function check(before: number): void {
  const after = progress().level;
  if (after > before) {
    celebrate(true);
    toast(m().passport.levelUp(m().passport.levels[after]));
  }
  bus.emit('change');
}

export function logWord(): void {
  today().words++;
}

export function logSpell(w: string): void {
  const lv = progress().level;
  const e = S().english;
  e.spelled[w] = (e.spelled[w] ?? 0) + 1;
  today().spells++;
  check(lv);
}

export function logShadow(w: string): void {
  const lv = progress().level;
  const e = S().english;
  e.shadowed[w] = (e.shadowed[w] ?? 0) + 1;
  today().words++;
  check(lv);
}

/** A finished role-play: 3 stars with no mistakes, 2 with one, 1 otherwise. */
export function logTalk(id: string, misses: number): number {
  const lv = progress().level;
  const e = S().english;
  const stars = misses === 0 ? 3 : misses === 1 ? 2 : 1;
  e.talks[id] = Math.max(e.talks[id] ?? 0, stars);
  today().talks++;
  check(lv);
  return stars;
}

export function logStory(id: string, score: number): void {
  const lv = progress().level;
  const e = S().english;
  e.stories[id] = Math.max(e.stories[id] ?? 0, score);
  today().talks++;
  check(lv);
}

/** Stamp for a place visited or a festival celebrated (once). Returns true if it's new. */
export function stamp(id: string): boolean {
  const e = S().english;
  if (e.stamps[id] !== undefined) return false;
  const lv = progress().level;
  e.stamps[id] = dayNumber();
  toast(m().passport.newStamp);
  check(lv);
  return true;
}

/** The last `n` game days, oldest first, for the weekly chart. */
export function recentDays(n = 6): { day: number; words: number; talks: number; spells: number }[] {
  const e = S().english;
  const d = dayNumber();
  return Array.from({ length: n }, (_, i) => {
    const k = d - (n - 1 - i);
    const v = e.days[k] ?? { words: 0, talks: 0, spells: 0 };
    return { day: k, words: v.words, talks: v.talks, spells: v.spells };
  });
}

/** For grown-ups: strongest words and the ones that keep slipping. */
export function wordReport(): { strong: string[]; weak: string[] } {
  const v = S().vocab;
  const ws = WORDS.filter((w) => v[w.w]);
  return {
    strong: ws.filter((w) => v[w.w].box >= 3).map((w) => w.w),
    weak: ws.filter((w) => v[w.w].box === 0).map((w) => w.w),
  };
}

/** Ordering food in English at the night market counts as a little conversation. */
export function logOrder(): void {
  today().talks++;
  bus.emit('change');
}
